import React from "react";
import useSEO from "@/hooks/useSEO";

const Privacy = () => {
  useSEO({
    title: "Privacy Policy",
    description: "Shaurya Travels privacy policy — how we collect, use and protect your personal information when you enquire about tours and travel services.",
    path: "/privacy",
  });

  return (
    <div data-testid="privacy-page" className="pt-32 pb-20">
      <section className="max-w-3xl mx-auto px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.4em] text-[var(--st-accent)] mb-4">— Legal</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl mb-3">Privacy Policy</h1>
        <p className="text-sm text-[var(--st-ink-2)] mb-10">Last updated: January 2026</p>

        <div className="prose prose-sm max-w-none text-[var(--st-ink-2)] space-y-6 leading-relaxed">
          <p>
            Shaurya Travels ("we", "our", "us") respects your privacy and is committed to protecting
            the personal information you share with us. This Privacy Policy explains how we collect,
            use, store and share your information when you visit our website
            <strong> https://shauryatravels.in</strong> or interact with our services.
          </p>

          <h2 className="font-serif-display text-2xl text-[var(--st-ink)] mt-10 mb-3">1. Information we collect</h2>
          <p>When you submit our enquiry form or contact us, we collect:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Your name</li>
            <li>Your contact number (required) and email (optional)</li>
            <li>The destination or service you are enquiring about</li>
            <li>Any message you choose to share</li>
            <li>Basic technical information (browser type, IP address, pages visited)</li>
          </ul>

          <h2 className="font-serif-display text-2xl text-[var(--st-ink)] mt-10 mb-3">2. How we use your information</h2>
          <p>We use the information you provide to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Respond to your travel enquiry by phone, WhatsApp or email</li>
            <li>Prepare a quotation and tour plan based on your preferences</li>
            <li>Confirm your booking and share itinerary details</li>
            <li>Comply with applicable Indian law and tax/GST requirements</li>
          </ul>

          <h2 className="font-serif-display text-2xl text-[var(--st-ink)] mt-10 mb-3">3. Information sharing</h2>
          <p>
            We do not sell, rent, or trade your personal information. We share your information
            only with trusted travel partners (hotels, transport providers, guides) strictly to
            fulfil your booking, and only the minimum information required to do so.
          </p>

          <h2 className="font-serif-display text-2xl text-[var(--st-ink)] mt-10 mb-3">4. Cookies & analytics</h2>
          <p>
            We use minimal cookies for basic site functionality and to understand how visitors use
            our site so we can improve it. You can disable cookies in your browser at any time.
          </p>

          <h2 className="font-serif-display text-2xl text-[var(--st-ink)] mt-10 mb-3">5. Data retention</h2>
          <p>
            Enquiries are retained for as long as is necessary to follow up and serve you, and
            thereafter as required by Indian tax/accounting law. You may request deletion of your
            enquiry by writing to us.
          </p>

          <h2 className="font-serif-display text-2xl text-[var(--st-ink)] mt-10 mb-3">6. Your rights</h2>
          <p>You may at any time:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Request access to the personal information we hold about you</li>
            <li>Request correction or deletion of that information</li>
            <li>Withdraw consent for receiving marketing communication</li>
          </ul>

          <h2 className="font-serif-display text-2xl text-[var(--st-ink)] mt-10 mb-3">7. Security</h2>
          <p>
            We use industry-standard security practices including HTTPS encryption, hashed
            passwords, and access controls to keep your data safe. However, no method of internet
            transmission is 100% secure.
          </p>

          <h2 className="font-serif-display text-2xl text-[var(--st-ink)] mt-10 mb-3">8. Children's privacy</h2>
          <p>
            Our services are intended for adults aged 18 and above. We do not knowingly collect
            information from children.
          </p>

          <h2 className="font-serif-display text-2xl text-[var(--st-ink)] mt-10 mb-3">9. Contact us</h2>
          <p>
            For any privacy-related question or request, please contact:<br />
            <strong>Shaurya Travels</strong><br />
            Email: <a href="mailto:shauryatravels1458@gmail.com" className="text-[var(--st-accent)]">shauryatravels1458@gmail.com</a><br />
            Phone: <a href="tel:+919371212211" className="text-[var(--st-accent)]">+91 9371212211</a><br />
            Pune, Maharashtra, India
          </p>

          <h2 className="font-serif-display text-2xl text-[var(--st-ink)] mt-10 mb-3">10. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. Any changes will be posted on this page
            with an updated "Last updated" date.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
